import { useCallback, useEffect, useState } from 'react';
import {
  clearDeferredInstallPrompt,
  dismissInstallPrompt,
  dismissIosHint,
  getDeferredInstallPrompt,
  isIosDevice,
  isStandaloneDisplay,
  promptPwaInstall,
  wasInstallRecentlyDismissed,
  wasIosHintRecentlyDismissed,
} from '../utils/pwaInstall';

/**
 * Tracks whether the app can be installed, and which path to use:
 * - hasInstallPrompt: Chrome/Edge deferred prompt is available (account menu)
 * - showInstallBanner: same, but not snoozed (snackbar)
 * - showIosHint: iOS Safari, not already installed, not snoozed
 * - isStandalone: already running as an installed app
 */
export default function usePwaInstall() {
  const [hasInstallPrompt, setHasInstallPrompt] = useState(() => Boolean(getDeferredInstallPrompt()));
  const [bannerDismissed, setBannerDismissed] = useState(() => wasInstallRecentlyDismissed());
  const [isStandalone, setIsStandalone] = useState(() => isStandaloneDisplay());
  const [showIosHint, setShowIosHint] = useState(
    () => isIosDevice() && !isStandaloneDisplay() && !wasIosHintRecentlyDismissed()
  );

  useEffect(() => {
    const syncPrompt = () => {
      setHasInstallPrompt(Boolean(getDeferredInstallPrompt()));
      setBannerDismissed(wasInstallRecentlyDismissed());
    };

    const handleInstalled = () => {
      clearDeferredInstallPrompt();
      setHasInstallPrompt(false);
      setShowIosHint(false);
      setIsStandalone(true);
    };

    // An event may already have been captured by the inline script in index.html
    // before React mounted — pick it up now.
    syncPrompt();

    window.addEventListener('pwa-install-available', syncPrompt);
    window.addEventListener('pwa-installed', handleInstalled);
    window.addEventListener('appinstalled', handleInstalled);

    const media = window.matchMedia('(display-mode: standalone)');
    const handleDisplayMode = () => {
      const standalone = isStandaloneDisplay();
      setIsStandalone(standalone);
      if (standalone) {
        setShowIosHint(false);
        setHasInstallPrompt(false);
      }
    };
    if (media.addEventListener) {
      media.addEventListener('change', handleDisplayMode);
    } else if (media.addListener) {
      media.addListener(handleDisplayMode);
    }

    return () => {
      window.removeEventListener('pwa-install-available', syncPrompt);
      window.removeEventListener('pwa-installed', handleInstalled);
      window.removeEventListener('appinstalled', handleInstalled);
      if (media.removeEventListener) {
        media.removeEventListener('change', handleDisplayMode);
      } else if (media.removeListener) {
        media.removeListener(handleDisplayMode);
      }
    };
  }, []);

  const install = useCallback(async () => {
    const result = await promptPwaInstall();
    if (result !== 'unavailable') {
      setHasInstallPrompt(false);
    }
    return result;
  }, []);

  const dismissAndroidPrompt = useCallback(() => {
    dismissInstallPrompt();
    setBannerDismissed(true);
  }, []);

  const dismissIos = useCallback(() => {
    dismissIosHint();
    setShowIosHint(false);
  }, []);

  const openIosHint = useCallback(() => {
    if (isIosDevice() && !isStandaloneDisplay()) {
      setShowIosHint(true);
      window.dispatchEvent(new Event('pwa-show-ios-hint'));
    }
  }, []);

  useEffect(() => {
    const handleShowIosHint = () => {
      if (isIosDevice() && !isStandaloneDisplay()) {
        setShowIosHint(true);
      }
    };

    window.addEventListener('pwa-show-ios-hint', handleShowIosHint);
    return () => window.removeEventListener('pwa-show-ios-hint', handleShowIosHint);
  }, []);

  return {
    hasInstallPrompt,
    showInstallBanner: hasInstallPrompt && !bannerDismissed && !isStandalone,
    showIosHint: showIosHint && !isStandalone,
    isStandalone,
    isIos: isIosDevice(),
    install,
    dismissAndroidPrompt,
    dismissIos,
    openIosHint,
  };
}
